import { useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { fmtBytes, fmtWhen, mono, secondaryButton } from '../proxy-vault/ui';

import type { RepositorySkillVersion } from '../../lib/api-client';

interface Props {
  versions: RepositorySkillVersion[];
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string | undefined;
  hasMore: boolean;
  onLoadMore: () => Promise<void>;
  /** The version the skill currently serves (● in the list). */
  currentVersionId: string | null;
  /** The version whose files are shown on the right. */
  viewedVersionId: string | null;
  onView: (versionId: string) => void;
  /** Rollback / roll-forward — PATCH current_version_id. */
  onSetCurrent: (versionId: string) => Promise<void>;
}

/**
 * Version history, newest first. Clicking a row shows that version's files;
 * "Set current" moves what conversations bind without touching the history
 * (versions are immutable — a rollback is a pointer move, never a rewrite).
 */
export function SkillVersionList({
  versions,
  status,
  error,
  hasMore,
  onLoadMore,
  currentVersionId,
  viewedVersionId,
  onView,
  onSetCurrent,
}: Props) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const setCurrent = async (versionId: string): Promise<void> => {
    setBusyId(versionId);
    setActionError(null);
    try {
      await onSetCurrent(versionId);
    } catch (err) {
      setActionError(describeApiError(err, String(err)));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="skrepo-versions">
      {status === 'loading' && versions.length === 0 && (
        <div style={{ padding: 12, fontSize: 12, color: 'var(--text-mute)' }}>Loading versions…</div>
      )}
      {status === 'error' && (
        <div style={{ padding: 12, fontSize: 12, color: 'var(--kind-failure)' }}>
          {error ?? 'Failed to load versions.'}
        </div>
      )}
      {actionError && (
        <div data-testid="skrepo-version-error" style={{ padding: '8px 12px', fontSize: 12, color: 'var(--kind-failure)' }}>
          {actionError}
        </div>
      )}
      {versions.map((v) => {
        const isCurrent = v.id === currentVersionId;
        const isViewed = v.id === viewedVersionId;
        return (
          <div
            key={v.id}
            data-testid={`skrepo-version-${v.version}`}
            role="button"
            tabIndex={0}
            aria-current={isViewed ? 'true' : undefined}
            onClick={() => onView(v.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onView(v.id);
            }}
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid var(--border)',
              background: isViewed ? 'var(--bg-alt2, var(--bg-alt))' : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span style={{ color: isCurrent ? 'var(--accent)' : 'var(--text-mute)' }}>{isCurrent ? '●' : '○'}</span>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>v{v.version}</span>
              <span style={{ color: 'var(--text-soft)' }}>{fmtWhen(v.created_at)}</span>
              <span style={{ color: 'var(--text-mute)', fontSize: 11 }}>
                {v.file_count} files · {fmtBytes(v.size_bytes)}
              </span>
              {!isCurrent && (
                <button
                  type="button"
                  data-testid={`skrepo-set-current-${v.version}`}
                  style={{ ...secondaryButton, marginLeft: 'auto', padding: '3px 8px', fontSize: 11 }}
                  disabled={busyId !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    void setCurrent(v.id);
                  }}
                >
                  {busyId === v.id ? 'Setting…' : 'Set current'}
                </button>
              )}
              {isCurrent && (
                <span style={{ marginLeft: 'auto', fontSize: 10, textTransform: 'uppercase', color: 'var(--accent)' }}>
                  current
                </span>
              )}
            </div>
            {v.summary && (
              <div style={{ fontSize: 11, color: 'var(--text-soft)', lineHeight: 1.4 }}>{v.summary}</div>
            )}
            <div style={{ fontSize: 10, color: 'var(--text-mute)', fontFamily: mono }} title={v.sha256}>
              {v.id} · sha256 {v.sha256.slice(0, 12)}…
            </div>
          </div>
        );
      })}
      {hasMore && (
        <button
          type="button"
          style={{ ...secondaryButton, margin: 12 }}
          onClick={() => void onLoadMore()}
        >
          Load more
        </button>
      )}
    </div>
  );
}
